import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual variant */
  variant?: 'default' | 'outlined' | 'elevated'
  children: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled Card component.
 *
 * Composable container for CardHeader, CardBody, and other content.
 * All visual state determined by props. Spreads ...props for HOC injection.
 *
 * @example
 * <Card variant="outlined">
 *   <CardHeader title="ITEM 1" showDelete onDelete={handleDelete} />
 *   <CardBody>
 *     <Select>...</Select>
 *   </CardBody>
 * </Card>
 */
const Card = React.memo(
  ({
    variant = 'default',
    children,
    style,
    className,
    ...props
  }: CardProps) => {
    const [css, theme] = useStyletron()

    const getVariantStyles = () => {
      switch (variant) {
        case 'elevated':
          return {
            border: 'none',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          }
        case 'outlined':
        case 'default':
        default:
          return {
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: theme.colors.grey30,
          }
      }
    }

    const cardClassName = css({
      backgroundColor: theme.colors.white,
      borderRadius: theme.borders.radius200,
      overflow: 'visible',
      ...getVariantStyles(),
    })

    return (
      <div
        className={`${cardClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {children}
      </div>
    )
  }
)

export default Card
